import React from "react";
import { ProductStarRating } from "@/features/product/ProductRating";
import { Skeleton } from "@mui/material";
import { Card, CardProps } from "@/components/Card";
import { Square } from "@/components/Square";
import { CoverImage } from "@/components/CoverImage";
import { ProductCardProduct } from "./ProductCard";

type ExpectedSkeletonData = {
  [Property in keyof ProductCardProduct]?: boolean;
};

export type SkeletonData = ExpectedSkeletonData & {
  imageHeight: number;
  imageWidth: number;
};

export interface SkeletonProductCardProps extends Omit<CardProps, "children"> {
  productCardValue: SkeletonData;
  compact?: boolean;
}

export const SkeletonProductCard = (props: SkeletonProductCardProps) => {
  const { productCardValue, compact, actions, cover, ...cardProps } = props;

  return (
    <Card
      data-id="product-card"
      {...cardProps}
      hoverable={false}
      bordered={cardProps.bordered ?? false}
    >
      <div
        className={`relative flex h-full ${
          compact ? "flex-row space-x-4" : "flex-col"
        }`}
      >
        {productCardValue.featuredImage && !compact && (
          <Skeleton height="auto" width="100%" variant="rectangular">
            <Square>
              <img
                data-id="product-card-image"
                alt={""}
                height={productCardValue.imageHeight}
                width={productCardValue.imageWidth}
              />
            </Square>
          </Skeleton>
        )}
        {productCardValue.featuredImage && compact && (
          <div className="basis-3/12">
            <Skeleton height="100%" width="100%" variant="rectangular">
              <CoverImage src={""} />
            </Skeleton>
          </div>
        )}
        <div
          data-id="product-card-content"
          className={`flex grow flex-col space-y-3 text-left ${
            compact ? "basis-9/12" : "mt-3"
          }`}
        >
          <div className="space-y-1">
            <div data-id="product-card-ratings">
              <ProductStarRating reviewScore={0} />
            </div>
            {productCardValue.type && <Skeleton variant="text" />}
            {productCardValue.title && <Skeleton variant="text" />}
          </div>
          {productCardValue.size && <Skeleton variant="text" />}
          {productCardValue.subscription && <Skeleton variant="text" />}
          {productCardValue.price && <Skeleton variant="text" />}
          {productCardValue.featuredText && (
            <>
              {!compact && (
                <hr data-id="product-card-divider" className="divider-x my-0" />
              )}
              <Skeleton variant="text" />
              <Skeleton variant="text" />
            </>
          )}
          {actions && (
            <Skeleton
              className="mt-4"
              variant="rectangular"
              height="auto"
              width="100%"
            />
          )}
        </div>
      </div>
    </Card>
  );
};
